import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take, delay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FeedbackService } from './feedback.service';
import { AuthStateService } from './auth-state.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  
  // List of endpoints that should not trigger refresh attempts
  private readonly noRefreshEndpoints = ['/login', '/refresh-token', '/auth/status'];

  constructor(
    private injector: Injector,
    private router: Router,
    private feedbackService: FeedbackService,
    private authStateService: AuthStateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip the interceptor for auth status check to prevent circular calls
    if (req.url.includes('/auth/status')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 401/403 errors (unauthorized or expired cookies)
        if (error.status === 401 || error.status === 403) {
          // Don't try to refresh on specific endpoints to avoid infinite loops
          if (this.noRefreshEndpoints.some(endpoint => req.url.includes(endpoint))) {
            return throwError(() => error);
          }
          
          return this.handleAuthError(req, next, error.status);
        }
        
        // Detect and handle CORS errors
        if (this.isCorsError(error)) {
          console.error('CORS Error detected:', error);
          
          // Check if this might be due to auth redirect
          if (this.mightBeAuthRedirect(req.url)) {
            console.warn('CORS error might be due to authentication redirect. Handling as authentication error.');
            return this.handleAuthError(req, next, 401);
          }
          
          this.feedbackService.emitError('Network error: Unable to connect to the server. Please try again or contact support.');
          return throwError(() => error);
        }
        
        return throwError(() => error);
      })
    );
  }
  
  /**
   * Checks if a URL is likely an API endpoint that might trigger auth redirects
   */
  private mightBeAuthRedirect(url: string): boolean {
    // Add patterns that match your API endpoints
    const apiPatterns = ['/api/', '/user/', '/me'];
    return apiPatterns.some(pattern => url.includes(pattern));
  }
  
  /**
   * Detects if an error is related to CORS issues
   */
  private isCorsError(error: any): boolean {
    // CORS errors often have these characteristics
    return (
      error instanceof HttpErrorResponse && 
      (error.status === 0 || error.status === undefined) &&
      (
        error.message.includes('CORS') || 
        error.message.includes('Cross-Origin Request Blocked') ||
        error.message.includes('NetworkError') ||
        error.message.includes('has been blocked by CORS policy')
      )
    );
  }
  
  private handleAuthError(req: HttpRequest<any>, next: HttpHandler, status: number): Observable<HttpEvent<any>> {
    // If we're not already refreshing
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(false);
      
      // Direct HTTP call to auth/status (bypass interceptors)
      return this.checkAuthStatusDirectly().pipe(
        switchMap(authStatus => {
          this.isRefreshing = false;
          
          if (authStatus.isAuthenticated) {
            // Session is still valid, retry the request
            this.refreshTokenSubject.next(true);
            return next.handle(req);
          } else {
            // Session is invalid, redirect to login
            this.authStateService.redirectToLogin(this.router.url);
            return throwError(() => new Error('Session expired'));
          }
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.authStateService.redirectToLogin(this.router.url);
          return throwError(() => err);
        })
      );
    } else {
      // Wait for the refresh to complete
      return this.refreshTokenSubject.pipe(
        filter(refreshed => refreshed),
        take(1),
        switchMap(() => next.handle(req))
      );
    }
  }
  
  /**
   * Makes a direct HTTP call to check auth status without going through interceptors
   * This avoids circular dependencies 
   */
  private checkAuthStatusDirectly() {
    // Use of() and delay to simulate a network request
    // In production, you would make a direct XHR or fetch call here
    return of({ isAuthenticated: true }).pipe(
      delay(100),
      switchMap(() => {
        const currentStatus = this.authStateService.getStoredAuthStatus();
        return of(currentStatus);
      })
    );
  }
}